<template>
    <header>
        <section class="bottom">
            <div class="bottom-title" style="width: auto;">
                <p>Profit & Loss Comparison</p>
            </div>

            <div style="margin-right: 20px;">
                <ejs-dropdownlist :width="100" :dataSource='currencies' :fields="{ text: 'value', value: 'key' }" v-model:value="currency"></ejs-dropdownlist>
            </div>

            <nav>
                <router-link to="/compare"><i class="fas fa-caret-left"></i> Back</router-link>
                <a href="#" v-on:click="save"><i class="fas fa-file-excel"></i> Export</a>
                <a href="#" v-show="!isBookmarked" v-on:click="createBookmark"><i class="fas fa-bookmark"></i> Bookmark</a>
                <a href="#" v-show="isBookmarked" v-on:click="removeBookmark"><i class="far fa-bookmark"></i> Remove Bookmark</a>
            </nav>
        </section>
    </header>

    <!--
        JG: Settings Notes:
        "allowEditing" - Needs to be true for formulas to calculate https://www.syncfusion.com/forums/109560/formulas-in-excel-spreadsheet-do-not-calculate-until-enable-editing-is-clicked
        "allowCellFormatting" - Needs to be true to allow column, cell formatting through code
        "allowNumberFormatting" - Needs to be true to allow formatting to two decimal places through code
        "cellEdit" - Event that ties into when a user tries to edit a cells. Used to instantly end editing do gget equivalent of allowEditing = false but with formulas.
    -->
    <ejs-spreadsheet id="spreadsheet" ref="spreadsheet" v-if="!resetting"
                     :allowEditing="true"
                     :allowCellFormatting="true"
                     :allowChart="false"
                     :allowConditionalFormat="false"
                     :allowDataValidation="false"
                     :allowDelete="true"
                     :allowFiltering="true"
                     :allowFindAndReplace="false"
                     :allowFreezePane="false"
                     :allowHyperlink="false"
                     :allowImage="false"
                     :allowInsert="true"
                     :allowMerge="false"
                     :allowNumberFormatting="true"
                     :allowOpen="false"
                     :allowResizing="true"
                     :allowSave="true"
                     :allowScrolling="true"
                     :allowSorting="true"
                     :allowUndoRedo="false"
                     :showFormulaBar="true"
                     :showRibbon="false"
                     :height="height"
                     :isLazyUpdate="false"
                     :cellEdit="cellEdit"></ejs-spreadsheet>
</template>

<script>
    import api from '../modules/ApiClient';
    import { SpreadsheetComponent } from "@syncfusion/ej2-vue-spreadsheet";
    import { DialogUtility } from '@syncfusion/ej2-popups';
    import Session from "../modules/Session";
    import { DropDownListComponent } from "@syncfusion/ej2-vue-dropdowns";
    import Bookmark from "../modules/Bookmark";
    import { createElement } from '@syncfusion/ej2-base';
    import { toRaw } from 'vue';
    import { ProfitAndLossModel } from '../models/ProfitAndLossModel';
    import { Currency, DialogMessages, ReportNames, Routes } from '../modules/Constants';

    export default {
        name: 'ProfitAndLossComparisonComponent',
        components: {
            "ejs-spreadsheet": SpreadsheetComponent,
            'ejs-dropdownlist': DropDownListComponent
        },
        methods: {
            save: function () {

                this.Dialog = DialogUtility.alert({
                    title: `Exporting ${ReportNames.PROFIT_AND_LOSS}`,
                    content: `Exporting ${ReportNames.PROFIT_AND_LOSS} to Excel. ${DialogMessages.PLEASE_WAIT}`,
                    okButton: { cssClass: 'hidden' },
                    showCloseIcon: false,
                    closeOnEscape: false,
                    animationSettings: { effect: 'Zoom' }
                });

                if (this.isFormattingReduced) {
                    let data = []
                    this.Tabs.forEach(tab => {
                        let sheet = {};
                        sheet.name = tab.TabName;
                        sheet.data = tab.Display;
                        data.push(sheet);
                    });

                    fetch(Routes.EXPORT_UNFORMATTED, {
                        method: "POST",
                        body: JSON.stringify(data),
                        responseType: 'blob'
                    })
                        .then(response => {
                            response.blob().then((data) => {
                                this.Dialog.hide()
                                var anchor = createElement('a', {
                                    attrs: {
                                        download: `${ReportNames.PROFIT_AND_LOSS}.xlsx`
                                    },
                                });
                                var url = URL.createObjectURL(data);
                                anchor.href = url;
                                document.body.appendChild(anchor);
                                anchor.click();
                                URL.revokeObjectURL(url);
                                document.body.removeChild(anchor);
                            })
                        })
                } else {
                    this.Tabs.sort((a, b) => b.TabIndex - a.TabIndex).forEach((tab, index) => {
                        setTimeout(() => { this.$refs.spreadsheet.goTo(`${tab.TabName}!A1`) }, this.tabLoadingTimer * index);
                    });
                    setTimeout(() => { this.$refs.spreadsheet.save({ fileName: `${ReportNames.PROFIT_AND_LOSS}.xlsx`, url: Routes.EXPORT_FORMATTED, saveType: "Xlsx" }) }, (this.Tabs.length + 1) * this.tabLoadingTimer);
                    setTimeout(() => { this.Dialog.hide() }, (this.Tabs.length + 2) * this.tabLoadingTimer);
                }
            },
            setSpreadsheetHeight: function () {
                this.height = document.documentElement.clientHeight - document.getElementById("spreadsheet").offsetTop - 1;
            },
            createBookmark: function () {
                let displayName = "";
                this.ncasData.forEach(set => {
                    displayName = displayName + " | " + set.version.ECRNumber.Value.Fields[0] + " " + set.version.Name;
                })

                Bookmark.CreateBookmark(this.$route.fullPath, `${ReportNames.PROFIT_AND_LOSS} ${displayName}`, new Date());
                this.isBookmarked = true;
                this.$emit(Bookmark.EventName);
            },
            removeBookmark: function () {
                Bookmark.RemoveBookmark(this.$route.fullPath);
                this.isBookmarked = false;
                this.$emit(Bookmark.EventName);
            },
            cellEdit: function () {
                //JG: Used to cancel and not allow the edit as we dont want users making changes, but do want formulas to run.
                this.$refs.spreadsheet.closeEdit();
            },
            displayError: function () {
                DialogUtility.alert({
                    title: `Error Loading ${ReportNames.PROFIT_AND_LOSS}`,
                    content: `There was an error while loading this ${ReportNames.PROFIT_AND_LOSS}. ${DialogMessages.ERROR}`,
                    okButton: { text: 'OK', click: function () { this.hide() } },
                    showCloseIcon: false,
                    closeOnEscape: false,
                    animationSettings: { effect: 'Zoom' }
                });
            },
            processTabs: function () {
                let data = toRaw(this.ncasData);
                data.currency = this.currency;
                this.Tabs.forEach(tab => {
                    tab.process(data)
                })

                //Handle Sheets
                if (this.Tabs.filter(x => x.isFormattingReduced === true).length > 0) {
                    this.isFormattingReduced = true;
                }

                if (this.isFormattingReduced === false) {
                    this.Tabs.sort((a, b) => b.TabIndex - a.TabIndex).forEach(tab => {
                        let data = tab.formatSheet();
                        
                        let sheet = {
                            name: tab.TabName,
                            rows: data.rows,
                            columns: data.columns
                        }

                        this.$refs.spreadsheet.insertSheet([sheet]);
                        this.$refs.spreadsheet.applyFilter();

                    });
                    this.$refs.spreadsheet.refresh();
                }

                this.Dialog.hide();
            }
        },
        data() {
            return {
                exchangeRates: [] , 
                currencies: [],
                currency: Currency.USD,
                height: 800,
                isBookmarked: false,
                tabLoadingTimer: 2500,
                ncasData: null,
                Tabs: [ProfitAndLossModel.ProfitAndLossAcrossOptions()],
                Dialog: null,
                isFormattingReduced: false,
                resetting: false
            };
        },
        computed: { },
        mounted: function () {
            document.title = `${ReportNames.TAB_PREFIX} - ${ReportNames.PROFIT_AND_LOSS}`;

            window.addEventListener('resize', this.setSpreadsheetHeight);
            this.setSpreadsheetHeight();
            this.isBookmarked = Bookmark.IsBookmarked(this.$route.fullPath);

            Session.WaitToBeAuthenticated(() => {
                    this.Dialog = DialogUtility.alert({
                        title: `Loading ${ReportNames.PROFIT_AND_LOSS}`,
                        content: `Loading ${ReportNames.PROFIT_AND_LOSS} data from NCAS. ${DialogMessages.PLEASE_WAIT}`,
                        okButton: { cssClass: 'hidden' },
                        showCloseIcon: false,
                        closeOnEscape: false,
                        animationSettings: { effect: 'Zoom' }
                    });



                Promise.all(this.$route.query.costStudies.map(cs => api.GetAvailableVersions(cs))).then(csVersions => {
                    let selectedVersions = this.$route.query.versions;
                    let versions = csVersions.reduce(function (a, b) { return a.concat(b) }, []).map(data => data.Version).filter(function (v) { return selectedVersions.indexOf(v.id) > -1; });

                    let apis = []
                    versions.forEach(version => {
                        let item = new Promise(function (resolve, reject) {
                            let data = {};

                            Promise.all([
                                api.GetCostStudy(version.costStudyId, version.id),
                                api.GetCalculations(version.costStudyId, version.id),
                                api.GetSnapshots(version.costStudyId, version.id)
                            ]).then(result => {
                                data.study = result[0];
                                data.calculations = result[1];
                                data.snapshots = result[2];
                                data.version = version;
                                resolve(data);
                            }).catch(e => {
                                reject(e);
                            });
                        });

                        apis.push(item);
                    });

                    Promise.all(apis).then(data => {
                        this.ncasData = data;

                        data.forEach((set, index) => {
                            let calculationWorker = new Worker(new URL('../workers/calculations-worker.js', import.meta.url));
                            calculationWorker.onmessage = ({ data }) => {
                                this.ncasData[index].calculations_parsed = toRaw(data);

                                let completed = true;
                                this.ncasData.forEach(dataset => {
                                    if (dataset.calculations_parsed === undefined) {
                                        completed = false;
                                    }
                                });

                                if (completed) {

                                    this.ncasData.forEach(dataset => {
                                        let fxRates = [];
                                        dataset.study.exchangeRates.forEach(rate => {
                                            rate.Item2.Values.forEach(item => {
                                                fxRates.push({
                                                    currency: rate.Item1,
                                                    year: new Date(item.Date).getUTCFullYear(),
                                                    value: item.Value
                                                });
                                            });
                                        });

                                        this.currencies = [...new Set(fxRates.map(x => x.currency))].map(x => {
                                            return { key: x, value: x }
                                        });

                                    })

                                    this.processTabs();
                                }
                                                                
                            };
                            calculationWorker.onerror = () => {
                                if (this.Dialog) {
                                    this.Dialog.hide();
                                }

                                this.displayError();
                            }

                            calculationWorker.postMessage(set.calculations.Calculations);
                        })
                    }).catch(() => {
                        this.displayError();
                    });

                }).catch(() => {
                    this.displayError();
                })
            });
        },
        unmounted() {
            window.removeEventListener('resize', this.setSpreadsheetHeight);
        },
        watch: {
            currency: function () {
                this.Dialog = DialogUtility.alert({
                    title: 'Running FX Conversion',
                    content: `Running FX Conversion on ${ReportNames.PROFIT_AND_LOSS}. ${DialogMessages.PLEASE_WAIT}`,
                    okButton: { cssClass: 'hidden' },
                    showCloseIcon: false,
                    closeOnEscape: false,
                    animationSettings: { effect: 'Zoom' }
                });

                if (this.isFormattingReduced === false) {
                    this.resetting = true;
                    this.$nextTick(() => {
                        this.resetting = false;
                    })
                }

                setTimeout(() => { this.processTabs() }, 2000)
            }
        }
    }
</script>